import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderInvoiceDetailModel } from 'src/app/models/shopping/provider-invoice-detail.model';
import { ProviderInvoiceModel } from 'src/app/models/shopping/provider-invoice.model';
import { ProviderInvoiceDetailRegisterModel } from 'src/app/models/tables/shopping/provider-invoice-detail-register.model';
import { ProviderInvoiceDetailTableModel } from 'src/app/models/tables/shopping/provider-invoice-detail-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ProviderInvoiceDetailService } from 'src/app/services/shopping/provider-invoice-detail.service';
import { ProviderInvoiceService } from 'src/app/services/shopping/provider-invoice.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-provider-invoice-detail',
  templateUrl: './provider-invoice-detail.component.html',
  styleUrls: ['./provider-invoice-detail.component.scss'],
  standalone: false
})
export class ProviderInvoiceDetailComponent implements OnInit, OnChanges {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly providerInvoiceService = inject(ProviderInvoiceService);
  private readonly providerInvoiceDetailService = inject(ProviderInvoiceDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() disableDetail: boolean = false;
  @Input('isFormView')
  set changeFormView(change: boolean) {
    if (change) this.createNewDetail();
  }
  @Input() permissionUpdatable: boolean = false;
  @Input() providerInvoiceDetailModel: ProviderInvoiceDetailModel = new ProviderInvoiceDetailModel();
  @Input() providerInvoiceModel: ProviderInvoiceModel = new ProviderInvoiceModel();
  @Input('updateListDetail')
  set changeUpdateListDetail(change: number) {
    if (change) this.getUpdateListDetail();
  }
  @Output() quantityDetails = new EventEmitter<number>();
  @Output() updateProviderInvoice = new EventEmitter<ProviderInvoiceModel>();

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<ProviderInvoiceDetailRegisterModel> = new PagedResponseModel<ProviderInvoiceDetailRegisterModel>();
  detailHeader: ColumnResponseModel[] = [];
  isFormView: boolean = false;
  limit: number = 10;
  page: number = 1;
  tableActions: TableAction[] = [];

  async ngOnInit() {
    await this.getColumnsForDetailsProviderInvoice();
    if (this.providerInvoiceModel) {
      await Promise.all([this.getUpdateListDetail()]);
    }
    this.tableActions = this.getDefaultActions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const disableDetail = changes['disableDetail'];
    if (disableDetail && !disableDetail.firstChange) {
      this.tableActions = this.getDefaultActions();
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.disableDetail) {
      return [];
    } else {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    }
  }

  async createNewDetail() {
    this.providerInvoiceDetailModel = new ProviderInvoiceDetailModel();
    this.getFormView(true);
  }

  async deleteDetail(selectedIds: number[]) {
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar el detalle seleccionado?`);
    if (accepted) {
      try {
        await firstValueFrom(this.providerInvoiceDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle fue eliminado exitosamente.');
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error al eliminar el detalle de la factura de proveedor.', error);
      }
    }
  }

  editDetail(item: ProviderInvoiceDetailTableModel) {
    this.providerInvoiceDetailModel = Object.assign(new ProviderInvoiceDetailModel(), item);
  }

  async getColumnsForDetailsProviderInvoice() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.providerInvoiceDetailService.getColumnsForDetailsProviderInvoice());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la tabla de captura detalle.', error);
    }
  }

  async getDetailsProviderInvoiceWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.providerInvoiceDetailService.getProviderInvoiceDetailsByProviderInvoiceIdWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.providerInvoiceModel.providerInvoiceId
      ));
      this.quantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al buscar los detalles de las facturas proveedores.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getProviderInvoice(providerInvoice: ProviderInvoiceModel) {
    this.updateProviderInvoice.emit(providerInvoice);
  }

  async getUpdateListDetail() {
    if (!this.providerInvoiceModel) return;
    try {
      this.page = 1;
      this.limit = 10;
      this.columnSort = null;
      this.ascendant = true;
      this.getDetailsProviderInvoiceWithPagination();
      this.clearSelectionTrigger++;
      this.isFormView = false;
    } catch (error) {
      console.error('Error al buscar el detalle de la factura de proveedor.', error);
    }
  }

  async getUpdateListDetailByDelete() {
    this.providerInvoiceDetailModel = new ProviderInvoiceDetailModel();
    this.clearSelectionTrigger++;

    try {
      const response = await firstValueFrom(
        this.providerInvoiceService.getProviderInvoiceById(this.providerInvoiceModel.providerInvoiceId)
      );
      if (response) {
        this.updateProviderInvoice.emit(response);
      }
    } catch (error) {
      console.error('Error al procesar la actualización de la lista de detalles.', error);
    }
  }

  async onAction(event: { action: string, item?: ProviderInvoiceDetailRegisterModel, selectedItems?: number[] }) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para modificar facturas proveedores.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (event.action === 'edit' && event.item) {
      const response = await firstValueFrom(this.providerInvoiceDetailService.getProviderInvoiceDetailById(event.item.id));
      if (response) {
        this.providerInvoiceDetailModel = response;
        this.isFormView = true;
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getDetailsProviderInvoiceWithPagination();
  }

}
